import { AccessorKeyColumnDef, createColumnHelper } from '@tanstack/react-table'
import { Member, MemberDevice } from '../../types/MemberTypes'
import { Button } from '@radix-ui/themes'
import { generateDemoView } from '../alert-console/AlertColumns'
import { formatUtcTimestamp } from '../../utils/dates'

export const memberColumns = (
  setShowPopout: (show: boolean) => void,
  setPopoutData: (memberInfo: Member) => void,
  showConditionalAccess?: boolean
) => {
  const columnHelper = createColumnHelper<Member>()

  const getDeviceCount = (devices: MemberDevice[], activeOnly: boolean) => {
    return devices
      .filter((device) => device.status === 'ACTIVE')
      .filter((device) => (activeOnly ? device.identifier !== null : device.identifier === null)).length
  }

  const generateCAStatus = (
    isBlocked?: boolean,
    failedToBlock?: boolean,
    gracePeriod?: string,
    blockedByPolicy?: string
  ) => {
    const now = new Date()
    const gracePeriodDate = new Date(gracePeriod ?? '')
    if (isBlocked && now < gracePeriodDate && !blockedByPolicy?.includes('AlertSeverity')) {
      return <p className="text-[#D6A35C]">In Grace Period</p>
    } else if (failedToBlock) {
      return <p className="text-[#d93d42]">Failed to Suspend</p>
    } else if (isBlocked) {
      return <p className="text-[#d93d42]">Suspended</p>
    } else {
      return <p className="text-mainText">Active</p>
    }
  }

  const columnHelpers = [
    columnHelper.accessor('name', {
      header: 'Name',
      cell: (row) => {
        return generateDemoView(row.row.original.name, row.row.original.demoMember ?? false, 'member')
      },
    }),
    columnHelper.accessor('email', {
      header: 'Email',
    }),
    columnHelper.accessor('devices', {
      cell: (info) => getDeviceCount(info.getValue(), true),
      header: 'Active Devices',
    }),
    columnHelper.accessor('devices', {
      cell: (info) => getDeviceCount(info.getValue(), false),
      header: 'Pending Devices',
    }),
    columnHelper.accessor('createdAt', {
      cell: (info) => formatUtcTimestamp(info.getValue()),
      header: 'Added (UTC)',
    }),
    {
      id: 'button',
      cell: (row: { row: { original: Member } }) => (
        <Button
          size={'2'}
          className="cursor-pointer float-right"
          onClick={() => {
            setPopoutData(row.row.original)
            setShowPopout(true)
          }}
          variant="outline"
        >
          View
        </Button>
      ),
    },
  ]

  if (showConditionalAccess) {
    const conditionalAccessColumn = columnHelper.accessor('isBlocked', {
      header: 'Conditional Access',
      cell: (row) => {
        return generateCAStatus(
          row.row.original.isBlocked,
          row.row.original.failedToBlock,
          row.row.original.gracePeriod,
          row.row.original.blockedByPolicy
        )
      },
    }) as AccessorKeyColumnDef<Member, string>
    columnHelpers.splice(1, 0, conditionalAccessColumn)
  }

  return columnHelpers
}
